import { type ReactElement, type ReactNode, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { toast } from 'sonner'
import { FullPageLoader } from '@/components/ui/loading-spinner'
import { useAuthStore } from '@/stores/auth'

export const SUPER_ADMIN_PERMISSION = 'analytics:view_all_organizations'

interface SuperAdminRouteProps {
  children: ReactNode
}

/**
 * Gates a route behind the `analytics:view_all_organizations` permission
 * (auto-granted to BlueConic employees via the Super Admin role).
 *
 * Behavior:
 * - Waits for the user to resolve before deciding (no flash).
 * - Renders children if the user holds the permission.
 * - Redirects to /dashboard with a toast otherwise.
 */
export function SuperAdminRoute({
  children,
}: SuperAdminRouteProps): ReactElement {
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const hasPermission = useAuthStore((s) => s.hasPermission)

  const ready = isAuthenticated() && !!user
  const allowed = ready && hasPermission(SUPER_ADMIN_PERMISSION)

  useEffect(() => {
    if (ready && !allowed) {
      toast.error('Super Admin access is required to view this page.')
    }
  }, [ready, allowed])

  if (!ready) {
    return <FullPageLoader message="Checking access…" />
  }

  if (!allowed) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}
